import LegalLayout, { LegalSection, legalLink } from "@/components/legal/LegalLayout";
import useSeo from "@/hooks/useSeo";
import { openCookieSettings } from "@/lib/consent";

const COOKIES = [
  ["wj_consent_v1", "WebJesters (local storage)", "Remembers your cookie choice so we don't ask again.", "Strictly necessary", "Until you clear it"],
  ["__stripe_mid, __stripe_sid", "Stripe", "Fraud prevention and session security during checkout. Set only when you open a Stripe checkout page.", "Strictly necessary", "Up to 1 year / session"],
  ["_gcl_au, _gcl_aw and related", "Google Ads (gtag.js)", "Measures whether a Google ad led to an inquiry or purchase. Loaded only after you click Accept.", "Advertising (optional)", "Up to 90 days"],
];

export default function CookiePolicy() {
  useSeo({
    title: "Cookie Policy — WebJesters",
    description:
      "Which cookies webjesters.com uses, what they do, and how to change your choice. Advertising cookies stay off until you accept; Global Privacy Control signals are honored.",
    path: "/cookie-policy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Cookie Policy", item: "https://webjesters.com/cookie-policy" },
      ],
    },
  });

  return (
    <LegalLayout title="Cookie Policy" eyebrow="Legal" lastUpdated="October 16, 2026">
      <p>
        This Cookie Policy explains how WebJesters LLC ("WebJesters," "we," "us," or "our") uses cookies and similar
        technologies on webjesters.com. It is part of our{" "}
        <a href="/privacy-policy" className={legalLink}>Privacy Policy</a>. Hosted client websites have their own
        cookie practices set by each client.
      </p>

      <LegalSection title="What cookies are">
        <p>
          Cookies are small text files a website stores in your browser. Similar technologies include local storage,
          pixels, and scripts that set or read identifiers. We use the word "cookies" for all of them here.
        </p>
      </LegalSection>

      <LegalSection title="Our default is off">
        <p>
          webjesters.com works without tracking cookies. Essential items run by default. The only optional category is
          advertising measurement from Google Ads, and that script is not loaded at all until you click{" "}
          <strong className="text-white">Accept</strong> in the cookie banner. If you click Decline, or never answer,
          it stays off.
        </p>
        <p>
          We honor the <strong className="text-white">Global Privacy Control (GPC)</strong> browser signal. If your
          browser sends it, we record a decline automatically and do not show the banner.
        </p>
      </LegalSection>

      <LegalSection title="Cookies we use" id="cookies">
        <div className="overflow-x-auto -mx-5 px-5">
          <table data-testid="cookie-table" className="w-full text-[13.5px] text-left border-collapse min-w-[640px]">
            <thead>
              <tr className="text-zinc-400 text-[11.5px] tracking-[0.14em] uppercase">
                <th className="py-2 pr-4 font-normal">Name</th>
                <th className="py-2 pr-4 font-normal">Set by</th>
                <th className="py-2 pr-4 font-normal">Purpose</th>
                <th className="py-2 pr-4 font-normal">Category</th>
                <th className="py-2 font-normal">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.07]">
              {COOKIES.map(([name, by, purpose, cat, dur]) => (
                <tr key={name} className="align-top">
                  <td className="py-3 pr-4 font-mono text-[12.5px] text-white">{name}</td>
                  <td className="py-3 pr-4 text-zinc-300">{by}</td>
                  <td className="py-3 pr-4 text-zinc-300">{purpose}</td>
                  <td className="py-3 pr-4 text-zinc-300">{cat}</td>
                  <td className="py-3 text-zinc-300">{dur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[13px] text-zinc-400">
          We do not use analytics suites, social media pixels, session-recording tools, or cross-site advertising networks
          on webjesters.com. If that changes, this page and the banner will be updated before anything new is set.
        </p>
      </LegalSection>

      <LegalSection title="Changing your choice">
        <p>
          You can change your decision any time. Use the{" "}
          <button type="button" onClick={openCookieSettings} data-testid="cookie-policy-settings" className={legalLink}>
            Cookie settings
          </button>{" "}
          control (also in the footer as "Do Not Sell or Share My Personal Information"), or clear this site's data in
          your browser, which removes the stored choice and any Google cookies. Most browsers also let you block cookies
          entirely; the site will keep working if you do.
        </p>
      </LegalSection>

      <LegalSection title="Is this a 'sale' or 'sharing' of personal information?">
        <p>
          We do not sell personal information for money. Under some state privacy laws (for example the California
          Consumer Privacy Act), allowing an advertising provider to set a cookie for cross-context behavioral advertising
          can count as "sharing." Because advertising cookies only run after you opt in, and you can withdraw that at any
          time, declining or using GPC is your opt-out. Details are in the{" "}
          <a href="/privacy-policy#state-rights" className={legalLink}>state privacy rights</a> section of our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about cookies? Email{" "}
          <a href="mailto:team@webjesters.com" className={legalLink}>team@webjesters.com</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
